import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const SecurityTab = ({ onPasswordChange, onTwoFactorToggle, initialTwoFactorEnabled = false }) => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(initialTwoFactorEnabled);
  const [showQRCode, setShowQRCode] = useState(false);
  const [errors, setErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [isEnabling2FA, setIsEnabling2FA] = useState(false);
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isRevokingSession, setIsRevokingSession] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(true);

  // Update 2FA state when prop changes
  useEffect(() => {
    setTwoFactorEnabled(initialTwoFactorEnabled);
  }, [initialTwoFactorEnabled]);

  // Fetch sessions on component mount
  useEffect(() => {
    fetchSessions();
  }, []);

  // Fetch security activity from backend
  const fetchSecurityActivity = async () => {
    try {
      setIsLoadingActivity(true);
      setErrors(prev => ({ ...prev, activity: '' }));

      // Import realApiService dynamically
      const { default: realApiService } = await import('../../../utils/realApiService.js');

      // Fetch security activity from backend
      const result = await realApiService.users.getSecurityActivity();

      // Normalize backend result: support {security_events: [...]} or {events: [...]} or {data: {security_events: [...]}}
      const events = Array.isArray(result?.security_events)
        ? result.security_events
        : Array.isArray(result?.events)
          ? result.events
          : Array.isArray(result?.data?.security_events)
            ? result.data.security_events
            : [];

      // Transform backend data to match frontend format
      const transformedActivity = events.map(event => ({
        id: event.id,
        action: formatSecurityEventAction(event.event_type, event.description),
        device: event.user_agent ? parseUserAgent(event.user_agent) : (event.event_source || 'Unknown Device'),
        location: event.ip_address || 'Unknown location',
        timestamp: formatTimestamp(event.created_at),
        ip: event.ip_address || 'Unknown',
        severity: event.severity,
        event_type: event.event_type,
        is_resolved: event.is_resolved
      }));

      setRecentActivity(transformedActivity);
    } catch (error) {
      console.error('Failed to fetch security activity:', error);
      setErrors(prev => ({
        ...prev,
        activity: error.message || 'Failed to load security activity. Please try again.'
      }));
    } finally {
      setIsLoadingActivity(false);
    }
  };

  // Helper function to format security event actions
  const formatSecurityEventAction = (eventType, eventData) => {
    const eventTypeMap = {
      'login_success': 'Successful login',
      'login_failure': 'Failed login attempt',
      'password_change': 'Password changed',
      'two_factor_enabled': 'Two-factor authentication enabled',
      'two_factor_disabled': 'Two-factor authentication disabled',
      'session_created': 'New session created',
      'session_revoked': 'Session ended',
      'profile_updated': 'Profile information updated',
      'suspicious_activity': 'Suspicious activity detected',
      'data_access': 'Data accessed',
      'permission_change': 'Permissions modified',
      'api_access': 'API access',
      'export_data': 'Data exported',
      'delete_data': 'Data deleted'
    };

    return eventTypeMap[eventType] || `Security event: ${eventType}`;
  };

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Fetch active sessions from backend
  const fetchSessions = async () => {
    try {
      setIsLoadingSessions(true);
      setErrors(prev => ({ ...prev, sessions: '' }));

      // Import realApiService dynamically
      const { default: realApiService } = await import('../../../utils/realApiService');

      // Fetch sessions from backend
      const result = await realApiService.users.getSessions();

      // Normalize backend result: support array or {sessions: [...]} wrapper
      const sessions = Array.isArray(result?.sessions)
        ? result.sessions
        : Array.isArray(result)
          ? result
          : [];

      // Transform backend data to match frontend format
      const transformedSessions = sessions.map(session => ({
        id: session.id,
        device: parseUserAgent(session.user_agent),
        location: session.ip_address || 'Unknown location',
        lastActive: formatLastActivity(session.last_activity),
        isCurrent: session.is_current,
        created_at: session.created_at,
        ip_address: session.ip_address,
        user_agent: session.user_agent
      }));

      setActiveSessions(transformedSessions);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setErrors(prev => ({
        ...prev,
        sessions: error.message || 'Failed to load active sessions. Please try again.'
      }));
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Helper function to parse user agent into readable device info
  const parseUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown Device';

    // Simple user agent parsing
    if (userAgent.includes('Chrome')) {
      if (userAgent.includes('Mobile')) return 'Chrome on Mobile';
      if (userAgent.includes('Windows')) return 'Chrome on Windows';
      if (userAgent.includes('Mac')) return 'Chrome on macOS';
      if (userAgent.includes('Linux')) return 'Chrome on Linux';
      return 'Chrome Browser';
    }
    if (userAgent.includes('Firefox')) {
      if (userAgent.includes('Mobile')) return 'Firefox on Mobile';
      if (userAgent.includes('Windows')) return 'Firefox on Windows';
      if (userAgent.includes('Mac')) return 'Firefox on macOS';
      return 'Firefox Browser';
    }
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      if (userAgent.includes('Mobile') || userAgent.includes('iPhone')) return 'Safari on iPhone';
      if (userAgent.includes('iPad')) return 'Safari on iPad';
      return 'Safari on macOS';
    }
    if (userAgent.includes('Edge')) return 'Edge Browser';

    return 'Unknown Browser';
  };

  // Helper function to format last activity time
  const formatLastActivity = (lastActivity) => {
    if (!lastActivity) return 'Unknown';

    const now = new Date();
    const activityDate = new Date(lastActivity);
    const diffMs = now - activityDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Active now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return activityDate.toLocaleDateString();
  };

  // Helper function to get security event icon
  const getSecurityEventIcon = (eventType, severity) => {
    if (severity === 'critical' || severity === 'high') return 'AlertTriangle';

    const iconMap = {
      'login_success': 'LogIn',
      'login_failure': 'AlertCircle',
      'password_change': 'Key',
      'two_factor_enabled': 'Shield',
      'two_factor_disabled': 'ShieldOff',
      'session_created': 'Monitor',
      'session_revoked': 'LogOut',
      'profile_updated': 'User',
      'suspicious_activity': 'AlertTriangle',
      'data_access': 'Eye',
      'permission_change': 'Settings',
      'api_access': 'Code',
      'export_data': 'Download',
      'delete_data': 'Trash2'
    };

    return iconMap[eventType] || 'Info';
  };

  // Helper function to get security event color
  const getSecurityEventColor = (severity) => {
    const colorMap = {
      'low': 'text-blue-500',
      'medium': 'text-yellow-500',
      'high': 'text-orange-500',
      'critical': 'text-red-500'
    };

    return colorMap[severity] || 'text-muted-foreground';
  };

  // Helper function to get severity badge color
  const getSeverityBadgeColor = (severity) => {
    const colorMap = {
      'low': 'bg-blue-100 text-blue-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-orange-100 text-orange-800',
      'critical': 'bg-red-100 text-red-800'
    };

    return colorMap[severity] || 'bg-gray-100 text-gray-800';
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'newPassword') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) {
      return;
    }

    setIsChangingPassword(true);
    setErrors({});

    try {
      await onPasswordChange(passwordForm);

      // Clear form on success
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength(0);

      // Show success message (you could add a toast notification here)
      console.log('Password changed successfully');
    } catch (error) {
      console.error('Failed to change password:', error);

      // Set error message
      setErrors({
        general: error.message || 'Failed to change password. Please try again.'
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleTwoFactorToggle = async () => {
    if (!twoFactorEnabled) {
      // Enable 2FA - get QR code from backend
      setIsEnabling2FA(true);
      setErrors({});

      try {
        // Import realApiService dynamically
        const { default: realApiService } = await import('../../../utils/realApiService');

        // Call backend to generate QR code
        const result = await realApiService.auth.enable2FA();

        setQrCodeData(result.qr_code_url);
        setTwoFactorSecret(result.secret);
        setShowQRCode(true);
      } catch (error) {
        console.error('Failed to enable 2FA:', error);
        setErrors({
          twoFactor: error.message || 'Failed to enable two-factor authentication. Please try again.'
        });
      } finally {
        setIsEnabling2FA(false);
      }
    } else {
      // Disable 2FA
      setTwoFactorEnabled(false);
      onTwoFactorToggle(false);
    }
  };

  const confirmTwoFactorSetup = async () => {
    if (!verificationToken.trim()) {
      setErrors({
        verification: 'Please enter the verification code from your authenticator app.'
      });
      return;
    }

    setIsVerifying2FA(true);
    setErrors({});

    try {
      // Import realApiService dynamically
      const { default: realApiService } = await import('../../../utils/realApiService');

      // Verify the token with backend
      await realApiService.auth.verify2FA(verificationToken.trim());

      // Success - enable 2FA
      setTwoFactorEnabled(true);
      setShowQRCode(false);
      setVerificationToken('');
      setQrCodeData(null);
      setTwoFactorSecret('');
      onTwoFactorToggle(true);

      console.log('Two-factor authentication enabled successfully');
    } catch (error) {
      console.error('Failed to verify 2FA token:', error);
      setErrors({
        verification: error.message || 'Invalid verification code. Please try again.'
      });
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  const handleEndSession = async (sessionId) => {
    try {
      setIsRevokingSession(sessionId);
      setErrors(prev => ({ ...prev, sessions: '' }));

      // Import realApiService dynamically
      const { default: realApiService } = await import('../../../utils/realApiService');

      // Revoke the session
      await realApiService.users.revokeSession(sessionId);

      // Remove the session from the list
      setActiveSessions(prev => prev.filter(session => session.id !== sessionId));

      console.log('Session ended successfully:', sessionId);
    } catch (error) {
      console.error('Failed to end session:', error);
      setErrors(prev => ({
        ...prev,
        sessions: error.message || 'Failed to end session. Please try again.'
      }));
    } finally {
      setIsRevokingSession(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Password Change Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Lock" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Change Password</h3>
        </div>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div className="relative">
            <Input
              label="Current Password"
              name="currentPassword"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={handlePasswordInputChange}
              error={errors.currentPassword}
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              className="absolute right-3 top-8 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPasswords.current ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>

          <div className="relative">
            <Input
              label="New Password"
              name="newPassword"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={handlePasswordInputChange}
              error={errors.newPassword}
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute right-3 top-8 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPasswords.new ? 'EyeOff' : 'Eye'} size={16} />
            </button>
            
            {passwordForm.newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Password strength</span>
                  <span className={`font-medium ${
                    passwordStrength < 50 ? 'text-error' : 'text-success'
                  }`}>
                    {getPasswordStrengthText()}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${getPasswordStrengthColor()}`}
                    style={{ width: `${passwordStrength}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Input
              label="Confirm New Password"
              name="confirmPassword"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordForm.confirmPassword}
              onChange={handlePasswordInputChange}
              error={errors.confirmPassword}
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute right-3 top-8 text-muted-foreground hover:text-foreground"
            >
              <Icon name={showPasswords.confirm ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>

          {errors.general && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{errors.general}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            iconName={isChangingPassword ? "Loader" : "Save"}
            iconPosition="left"
            className="mt-6"
            disabled={isChangingPassword}
          >
            {isChangingPassword ? 'Updating Password...' : 'Update Password'}
          </Button>
        </form>
      </div>

      {/* Active Sessions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Monitor" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Active Sessions</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSessions}
            iconName={isLoadingSessions ? "Loader" : "RefreshCw"}
            iconPosition="left"
            disabled={isLoadingSessions}
          >
            {isLoadingSessions ? 'Loading...' : 'Refresh'}
          </Button>
        </div>

        {errors.sessions && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{errors.sessions}</p>
          </div>
        )}

        {isLoadingSessions ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" size={20} className="animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading sessions...</span>
          </div>
        ) : activeSessions.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Monitor" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">No active sessions found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeSessions.map((session) => (
              <div key={session.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name="Monitor" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {session.device}
                      {session.isCurrent && (
                        <span className="ml-2 px-2 py-1 bg-success text-success-foreground text-xs rounded-full">
                          Current
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {session.location} • {session.lastActive}
                    </p>
                  </div>
                </div>
                {!session.isCurrent && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEndSession(session.id)}
                    iconName={isRevokingSession === session.id ? "Loader" : "X"}
                    iconPosition="left"
                    disabled={isRevokingSession === session.id}
                  >
                    {isRevokingSession === session.id ? 'Ending...' : 'End Session'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default SecurityTab;